import {
  getReservationVisibleTone,
  mapLocalReservationSignatureStatus,
  mapSalesforceReservationStatus,
  normalizeSalesforceStatus,
  ReservationStatus,
  ReservationVisibleTone,
  RESERVATION_STATUS_TEST_CASES,
  type LocalReservationSignatureStatus,
} from '@/core/reservation';
import type { AvailableUnit } from '@/core/api/models/properties';
import { getIsAdhaJourney } from '@/core/hooks/useIsAdhaJourney';
import {
  getValue,
  isAdhaReservationStatusCopyReady,
} from '@/core/services/translationService';
import { I18nManager } from 'react-native';
import {
  getUnitDetailsLabels,
  type PropertyTranslateFn,
} from './getPropertyLabels';

/**
 * True when `t('reservationStatus.<key>')` resolved to a real string
 * (including ADHA `adha.reservationStatus.*` via useTranslation).
 * Missing keys return the path itself from getNestedValue.
 */
function isContentfulStatusMessageHit(
  value: string,
  lookupKey: string,
): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  if (trimmed === lookupKey || trimmed === `adha.${lookupKey}`) {
    return false;
  }
  if (
    trimmed.startsWith('reservationStatus.') ||
    trimmed.startsWith('adha.reservationStatus.')
  ) {
    return false;
  }
  return true;
}

function lookupReservationStatusKey(
  translate: PropertyTranslateFn,
  statusKey: string,
): string | null {
  const lookupKey = `reservationStatus.${statusKey}`;
  const statusValue = translate(lookupKey as any);
  if (isContentfulStatusMessageHit(statusValue, lookupKey)) {
    return statusValue.trim();
  }
  return null;
}

/**
 * Contentful (`adhaReservationStatus`) per-status message lookup only —
 * `reservationStatus.<lowercased current_status>`. `current_status` is
 * lowercased the same way the Contentful key is stored, so `Booked` matches
 * a key saved as `Booked` or `booked`.
 *
 * Same on web and mobile: no `reservationStatus.default` fallback here — a
 * status with no dedicated CF entry falls through to the raw SF status text
 * instead (see `resolveRawStatusFallbackMessage`), never a generic CF line.
 * Returns null when the key is absent — caller may then fall back
 * **only after** the Contentful fetch has settled.
 */
function resolveContentfulStatusMessage(
  translate: PropertyTranslateFn,
  rawStatus: string,
): string | null {
  const normalizedStatus = normalizeSalesforceStatus(rawStatus);
  if (!normalizedStatus) {
    return null;
  }
  return lookupReservationStatusKey(translate, normalizedStatus);
}

/** Local last-resort copy — used only after Contentful settles with no usable entry. */
function resolveLocalDefaultStatusMessage(
  translate: PropertyTranslateFn,
): string {
  return translate('reserveUnit.reservationVisible.reservationFormSent' as any);
}

/**
 * Display fallback for a raw SF/profile-details status when Contentful has
 * no dedicated `reservationStatus.<status>` entry for it — e.g. a status
 * value nobody has added copy for yet. Underscore/snake_case or all-caps
 * identifiers (legacy Salesforce values) are title-cased word by word;
 * already human-authored values (profile-details canonical strings like
 * "SPA Under Customer Signature") are left as-is so acronyms like "SPA"
 * aren't re-cased. Same on web and mobile.
 */
function resolveRawStatusFallbackMessage(rawStatus: string): string | null {
  const normalized = rawStatus.trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  if (!normalized) {
    return null;
  }
  const looksHumanCased = /[a-z]/.test(normalized) && /[A-Z]/.test(normalized);
  if (looksHumanCased) {
    return normalized;
  }
  return normalized
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * ADHA: wait for `adhaReservationStatus` Contentful fetch.
 * Non-ADHA: that API is not called — treat as ready (local default may apply).
 */
export function isReservationStatusMessageReady(): boolean {
  if (!getIsAdhaJourney()) {
    return true;
  }
  return isAdhaReservationStatusCopyReady();
}

/**
 * Testing only — pick a case (0–8). Shared by listing + unit details (web).
 * 0 = not reserved.
 * 1 Booking form Sent → Awaiting Signature
 * 2 Booking form Signed → Booking Form Signed
 * 3 Reservation Form Pending Sign → Reservation Form Sent
 * 4 Reservation Form Signed → Reservation Form Signed
 * 5 SPA Under Customer Signature → Awaiting SPA Signature
 * 6 SPA Counter Sign Pending → Awaiting Bloom SPA Signature
 * 7 SPA Counter Signed → SPA Signed
 * 8 Customer Signed SPA → SPA Signed
 */
export const TEST_RESERVATION_CASE = 0;
const TEST_RESERVATION_STATUS =
  RESERVATION_STATUS_TEST_CASES[TEST_RESERVATION_CASE] ?? '';

export interface ReservationVisibleCopy {
  readonly listing: string;
  /**
   * Null only while Contentful is still loading, or when there is no raw
   * status at all. After ready: Contentful per-status entry → raw SF status
   * (title-cased) → local bootstrap.
   */
  readonly listingMessage: string | null;
  readonly detailTitle: string;
  /** Same resolution rules as `listingMessage`. */
  readonly detailMessage: string | null;
  /** False until ADHA `adhaReservationStatus` Contentful fetch settles. */
  readonly isMessageReady: boolean;
}

export function resolveWebReservationRawStatus(
  listing?: AvailableUnit | null,
): string | undefined {
  const fromListing = listing?.reservation_status?.trim();
  // Live booking data always wins. The numbered constant remains a QA override.
  return TEST_RESERVATION_STATUS || fromListing || undefined;
}

export function resolveWebReservationMappedStatus(
  listing?: AvailableUnit | null,
  options?: {
    isLocallyReserved?: boolean;
    localSignatureStatus?: LocalReservationSignatureStatus | null;
  },
): ReservationStatus | null {
  const fromSalesforce = mapSalesforceReservationStatus(
    resolveWebReservationRawStatus(listing),
  );
  if (fromSalesforce) {
    return fromSalesforce;
  }
  return mapLocalReservationSignatureStatus(
    Boolean(options?.isLocallyReserved),
    options?.localSignatureStatus,
  );
}

export function isWebUnitReserved(
  listing?: AvailableUnit | null,
  isLocallyReserved = false,
): boolean {
  return (
    resolveWebReservationMappedStatus(listing, { isLocallyReserved }) != null
  );
}

export function getReservationVisibleCopy(
  mappedStatus: ReservationStatus | null,
  t?: PropertyTranslateFn,
  rawStatus?: string | null,
): ReservationVisibleCopy | null {
  if (!mappedStatus) {
    return null;
  }
  const translate =
    t ||
    ((key: any) => getValue(I18nManager.isRTL ? 'ar' : 'en', key));
  // Titles/tone shell still come from the enum map; status *messages* wait on Contentful.
  const structure =
    getUnitDetailsLabels(translate as PropertyTranslateFn).RESERVATION_VISIBLE[
      mappedStatus
    ];
  const isMessageReady = isReservationStatusMessageReady();
  const titles = {
    listing: structure.listing,
    detailTitle: structure.detailTitle,
  };

  const trimmedStatus = rawStatus?.trim() ?? '';
  if (!trimmedStatus) {
    return {
      ...titles,
      listingMessage: null,
      detailMessage: null,
      isMessageReady: true,
    };
  }

  if (!isMessageReady) {
    return {
      ...titles,
      listingMessage: null,
      detailMessage: null,
      isMessageReady: false,
    };
  }

  const message =
    resolveContentfulStatusMessage(translate as PropertyTranslateFn, trimmedStatus) ??
    resolveRawStatusFallbackMessage(trimmedStatus) ??
    resolveLocalDefaultStatusMessage(translate as PropertyTranslateFn);

  return {
    ...titles,
    listingMessage: message,
    detailMessage: message,
    isMessageReady: true,
  };
}

/**
 * Localized status line for a raw profile/API status.
 * After Contentful settles: CF per-status entry → raw SF status (title-cased)
 * → local `reserveUnit.reservationVisible.reservationFormSent`.
 * Returns null while Contentful is still loading, and null when the status
 * string is empty.
 */
export function getReservationStatusDisplayMessage(
  rawStatus: string | null | undefined,
  t?: PropertyTranslateFn,
): string | null {
  const mapped = mapSalesforceReservationStatus(rawStatus);
  if (!mapped) {
    return null;
  }
  const copy = getReservationVisibleCopy(mapped, t, rawStatus);
  if (!copy?.isMessageReady) {
    return null;
  }
  return copy.listingMessage;
}

export function formatListingCardMessage(
  copy: ReservationVisibleCopy,
): string | null {
  return copy.listingMessage;
}

export function getReservationCardHeading(
  mappedStatus: ReservationStatus,
  t?: PropertyTranslateFn,
): string {
  const translate = t || ((key: any) => getValue(I18nManager.isRTL ? 'ar' : 'en', key));
  const headings = getUnitDetailsLabels(translate as PropertyTranslateFn);
  const tone = getReservationVisibleTone(mappedStatus);
  if (tone === ReservationVisibleTone.PENDING) {
    return headings.RESERVATION_PENDING;
  }
  if (tone === ReservationVisibleTone.CONFIRMED) {
    return headings.RESERVATION_CONFIRMED;
  }
  return headings.RESERVATION_IN_PROGRESS;
}
